import { Dispatch } from 'react';
import { PaginateList } from 'src/app/common/types';
import { apiClient, apiErrorHandler, ADMIN_URL } from 'src/app/common/network';
import { getQueryPath } from 'src/app/common/utils';
import {
  BdmBatchFile,
  BdmBatchFileListItem,
  BdmHoldPolicyListItem,
  BdmSubmittedBatchFile,
} from '../types/opm-pd-bdm-types';

const opmPdBdmEndpoint = 'opm-pd/bdm/';

export type FetchBdmBatchFilesParams = {
  page: number;
  limit: number;
  batchStatus?: string;
  submittedAtFrom?: string;
  submittedAtTo?: string;
  sort?: string;
};

type UploadBdmBatchFileParams = {
  file: File;
};

type SubmitBdmBatchFilePoliciesParams = {
  id: string;
  blobStorage?: any;
  notes?: string;
  polices: {
    policyNumber: string;
    selectedAgent?: string;
    selectedAgentName?: string;
    reasonSelectedAnotherAgent?: string;
  }[];
  policesDeleted?: string[];
};

export type FetchBdmHoldPoliciesParams = {
  page: number;
  limit: number;
  sort?: string;
  policyIds?: string[];
  currentAgent?: string;
  soldUnit?: string;
  importedAgent?: string;
  suggestedAgent?: string;
  otherQualifiedAgent?: string;
  batchNo?: string;
  holdStartDate?: string;
  holdExpireDate?: string;
  reasonSentBack?: string;
  policyNumbers?: string[];
  reasonSelectedAnotherAgent?: string;
  sentBackFrom?: string;
  policyTaskStatus?: string;
};

export type EndBdmHoldPolicyCasesParams = {
  policyIds: string[];
};

export type ReassignBdmHoldPoliciesParams = {
  policies: {
    policyId: string;
    selectedAgent?: string;
    selectedAgentName?: string;
    reasonSelectedAnotherAgent?: string;
  }[];
  policiesDeleted?: string[];
};

export const fetchBdmBatchFiles = async (
  params: FetchBdmBatchFilesParams,
  dispatch?: Dispatch<any>,
): Promise<PaginateList<BdmBatchFileListItem>> => {
  // const queryPath = getQueryPath(`${ADMIN_URL}/${opmPdBdmEndpoint}batch-files?`, params);
  const url = `${ADMIN_URL}/${opmPdBdmEndpoint}batch-files`;
  return apiClient
    .get(url, {
      params: {
        ...params,
      },
    })
    .then((response) => {
      return response.data.data as PaginateList<BdmBatchFileListItem>;
    })
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchBdmBatchFile = async (id: string, dispatch?: Dispatch<any>): Promise<BdmBatchFile> => {
  return apiClient
    .get(`${ADMIN_URL}/${opmPdBdmEndpoint}batch-files/${id}`)
    .then((response) => response.data.data as BdmBatchFile)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchBdmSubmittedBatchFile = async (
  id: string,
  dispatch?: Dispatch<any>,
): Promise<BdmSubmittedBatchFile> => {
  return apiClient
    .get(`${ADMIN_URL}/${opmPdBdmEndpoint}batch-files/${id}/submitted-batch-file`)
    .then((response) => response.data.data as BdmSubmittedBatchFile)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const downloadBdmBatchFileTemplateFile = async (_?: undefined, dispatch?: Dispatch<any>): Promise<Blob> => {
  return apiClient
    .get<Blob>(`${ADMIN_URL}/${opmPdBdmEndpoint}batch-files/download-sample-file`, {
      responseType: 'blob',
    })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const exportBdmBatchFileSubmittedPolicies = async (
  params: { batchIds: string[] },
  dispatch?: Dispatch<any>,
): Promise<Blob> => {
  const queryPath = getQueryPath(`${ADMIN_URL}/${opmPdBdmEndpoint}batch-files/export-submitted-policies?`, params);
  return apiClient
    .get<Blob>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const exportBdmBatchFileFinalPolicies = async (
  params: { batchIds: string[] },
  dispatch?: Dispatch<any>,
): Promise<Blob> => {
  const queryPath = getQueryPath(`${ADMIN_URL}/${opmPdBdmEndpoint}batch-files/export-final-policies?`, params);
  return apiClient
    .get<Blob>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const uploadBdmBatchFile = async (
  params?: UploadBdmBatchFileParams,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  const { file } = params || {};
  const formData = new FormData();
  if (file) {
    formData.append('file', file);
  }
  return apiClient
    .post<void>(`${ADMIN_URL}/${opmPdBdmEndpoint}batch-files/upload`, formData)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const submitBdmBatchFilePolicies = async (
  payload: SubmitBdmBatchFilePoliciesParams,
  dispatch?: Dispatch<any>,
): Promise<PaginateList<BdmBatchFileListItem>> => {
  const { id, ...body } = payload;
  const url = `${ADMIN_URL}/${opmPdBdmEndpoint}batch-files/${id}/submit-policies`;
  return apiClient
    .post(url, body)
    .then((response) => {
      return response.data.data;
    })
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const cancelBdmBatchFile = async (
  payload: { id: string },
  dispatch?: Dispatch<any>,
): Promise<PaginateList<BdmBatchFileListItem>> => {
  const { id } = payload;
  const url = `${ADMIN_URL}/${opmPdBdmEndpoint}batch-files/${id}/cancel`;
  return apiClient
    .post(url)
    .then((response) => {
      return response.data.data;
    })
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchBdmHoldPolicies = async (
  params: FetchBdmHoldPoliciesParams,
  dispatch?: Dispatch<any>,
): Promise<PaginateList<BdmHoldPolicyListItem>> => {
  // const queryPath = getQueryPath(`${ADMIN_URL}/${opmPdBdmEndpoint}batch-files?`, params);
  const url = `${ADMIN_URL}/${opmPdBdmEndpoint}hold-policies`;
  return apiClient
    .post(url, params)
    .then((response) => {
      return response.data.data as PaginateList<BdmHoldPolicyListItem>;
    })
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const endBdmHoldPolicyCases = async (
  params: EndBdmHoldPolicyCasesParams,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  const url = `${ADMIN_URL}/${opmPdBdmEndpoint}hold-policies/end-cases`;
  return apiClient
    .post(url, params)
    .then((response) => {
      return response.data.data;
    })
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const reassignBdmHoldPolicies = async (payload: ReassignBdmHoldPoliciesParams, dispatch?: Dispatch<any>) => {
  const { ...body } = payload;
  const url = `${ADMIN_URL}/${opmPdBdmEndpoint}hold-policies/reassign`;
  return apiClient
    .post(url, body)
    .then((response) => {
      return response.data.data;
    })
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const exportBdmBatchFilePolicies = async (batchId: string, dispatch?: Dispatch<any>): Promise<Blob> => {
  return apiClient
    .get<Blob>(`${ADMIN_URL}/${opmPdBdmEndpoint}batch-files/${batchId}/export-policies`, {
      responseType: 'blob',
    })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const exportBdmHoldPolicies = async (_?: undefined, dispatch?: Dispatch<any>): Promise<Blob> => {
  return apiClient
    .get<Blob>(`${ADMIN_URL}/${opmPdBdmEndpoint}hold-policies/export`, {
      responseType: 'blob',
    })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};
